'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { ChevronDown, ChevronUp, Plus, BookMarked, Eye, ExternalLink, BarChart2, Volume2, X, Lightbulb, ArrowRight, Target } from 'lucide-react';
import type { EraData, TopicData } from './WallPage';

interface WallGridProps {
  eras: EraData[];
  coverageFilter: 'all' | 'gaps' | 'covered';
  newlyFilledTopics?: Set<string>;
}

// Story idea seeds per topic keyword
function getStoryIdeas(topicLabel: string, eraLabel: string): string[] {
  const label = topicLabel.toLowerCase();
  const ideas: string[] = [];

  if (label.includes('oral') || label.includes('tradition') || label.includes('law')) {
    ideas.push(`Record an elder's account of ${topicLabel} passed down through generations`);
    ideas.push(`Document how ${topicLabel} shaped community governance in ${eraLabel}`);
    ideas.push(`Collect proverbs or songs that encode knowledge of ${topicLabel}`);
  } else if (label.includes('trade') || label.includes('gold') || label.includes('route')) {
    ideas.push(`Trace a trade route connected to ${topicLabel} using oral or written sources`);
    ideas.push(`Interview descendants of traders who participated in ${topicLabel}`);
    ideas.push(`Map the economic impact of ${topicLabel} on communities in ${eraLabel}`);
  } else if (label.includes('migration') || label.includes('movement') || label.includes('nguni')) {
    ideas.push(`Document a family lineage that traces back to the ${topicLabel} period`);
    ideas.push(`Record oral histories about displacement or resettlement during ${topicLabel}`);
    ideas.push(`Explore how ${topicLabel} shaped cultural identity in ${eraLabel}`);
  } else if (label.includes('resistance') || label.includes('war') || label.includes('uprising')) {
    ideas.push(`Collect first-hand or inherited accounts of participation in ${topicLabel}`);
    ideas.push(`Document the role of women and youth in ${topicLabel}`);
    ideas.push(`Explore how ${topicLabel} is remembered in local communities today`);
  } else if (label.includes('mission') || label.includes('church') || label.includes('religion')) {
    ideas.push(`Interview community members about the legacy of ${topicLabel} in their village`);
    ideas.push(`Document how ${topicLabel} changed local customs during ${eraLabel}`);
    ideas.push(`Collect stories of resistance or adaptation to ${topicLabel}`);
  } else if (label.includes('land') || label.includes('farm') || label.includes('agri')) {
    ideas.push(`Document how land was used and managed during the ${topicLabel} period`);
    ideas.push(`Record oral histories about land dispossession or cultivation in ${eraLabel}`);
    ideas.push(`Explore the relationship between ${topicLabel} and food sovereignty`);
  } else if (label.includes('women') || label.includes('gender') || label.includes('family')) {
    ideas.push(`Collect stories of women's roles and leadership during ${topicLabel}`);
    ideas.push(`Document how family structures were shaped by ${topicLabel} in ${eraLabel}`);
    ideas.push(`Record oral histories about marriage, kinship, and identity in this period`);
  } else {
    ideas.push(`Share an oral history or personal account related to ${topicLabel}`);
    ideas.push(`Document a community memory or artifact connected to ${topicLabel} in ${eraLabel}`);
    ideas.push(`Contribute a written, audio, or photographic record about ${topicLabel}`);
  }

  // Always add a general prompt
  ideas.push(`Submit a primary source, photograph, or document that sheds light on ${topicLabel}`);

  return ideas.slice(0, 3);
}

// Gap Panel — slides in from the right
interface GapPanelProps {
  topic: TopicData;
  eraId: string;
  eraLabel: string;
  onClose: () => void;
}

function GapPanel({ topic, eraId, eraLabel, onClose }: GapPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const needed = topic.minCoverage - topic.published;
  const ideas = getStoryIdeas(topic.label, eraLabel);
  const contributionHref = `/contribution-submission?era=${eraId}&topic=${encodeURIComponent(topic.label)}`;

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        style={{ backgroundColor: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(2px)' }}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed right-0 top-0 h-full z-50 flex flex-col overflow-hidden"
        style={{
          width: 'min(420px, 100vw)',
          backgroundColor: 'var(--card)',
          borderLeft: '1px solid var(--border)',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.18)',
          animation: 'slideInRight 0.22s ease-out',
        }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between px-5 py-4 border-b flex-shrink-0"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--muted)' }}
        >
          <div className="flex-1 min-w-0 pr-3">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: '#E05A4720', color: '#B03020', fontSize: '0.65rem' }}
              >
                Gap
              </span>
              <span
                className="text-xs"
                style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', fontSize: '0.65rem' }}
              >
                {eraLabel}
              </span>
            </div>
            <h2 className="text-base font-semibold leading-tight" style={{ color: 'var(--foreground)' }}>
              {topic.label}
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
              This story gap needs your contribution to be filled.
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-1.5 rounded-lg transition-colors duration-150"
            style={{ color: 'var(--muted-foreground)' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--border)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            aria-label="Close panel"
          >
            <X size={16} />
          </button>
        </div>

        {/* Contribution count needed */}
        <div
          className="mx-5 mt-5 rounded-xl p-4 flex items-center gap-4 flex-shrink-0"
          style={{ backgroundColor: '#E05A4710', border: '1px solid #E05A4730' }}
        >
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#E05A4720' }}
          >
            <Target size={22} style={{ color: '#E05A47' }} />
          </div>
          <div>
            <p className="text-2xl font-bold tabular-nums" style={{ color: '#E05A47', fontFamily: 'var(--font-mono)' }}>
              {needed}
            </p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              contribution{needed !== 1 ? 's' : ''} needed to fill this gap
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', fontSize: '0.6rem' }}>
              {topic.published} of {topic.minCoverage} submitted so far
            </p>
          </div>
        </div>

        {/* Story ideas */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb size={14} style={{ color: '#F39C12' }} />
            <h3 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
              Story ideas for this gap
            </h3>
          </div>
          <div className="space-y-3">
            {ideas.map((idea, i) => (
              <div
                key={i}
                className="rounded-xl p-3.5"
                style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}
              >
                <div className="flex items-start gap-2.5">
                  <span
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                    style={{ backgroundColor: '#F39C1220', color: '#F39C12', fontFamily: 'var(--font-mono)' }}
                  >
                    {i + 1}
                  </span>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--foreground)' }}>
                    {idea}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Progress bar */}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Coverage progress</span>
              <span
                className="text-xs tabular-nums"
                style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', fontSize: '0.65rem' }}
              >
                {topic.published}/{topic.minCoverage}
              </span>
            </div>
            <div className="h-2 rounded-full w-full" style={{ backgroundColor: 'var(--muted)' }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, Math.round((topic.published / topic.minCoverage) * 100))}%`,
                  backgroundColor: '#E05A4766',
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div
          className="px-5 py-4 border-t flex-shrink-0"
          style={{ borderColor: 'var(--border)', backgroundColor: 'var(--muted)' }}
        >
          <Link
            href={contributionHref}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200"
            style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            <Plus size={16} />
            Start Contributing
            <ArrowRight size={14} className="ml-1" />
          </Link>
          <p className="text-center text-xs mt-2" style={{ color: 'var(--muted-foreground)', fontSize: '0.65rem' }}>
            Pre-filled with era: <strong>{eraLabel}</strong> · topic: <strong>{topic.label}</strong>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}

function TopicBlock({
  topic,
  eraId,
  eraLabel,
  onGapClick,
  isNewlyFilled,
}: {
  topic: TopicData;
  eraId: string;
  eraLabel: string;
  onGapClick: (topic: TopicData, eraId: string, eraLabel: string) => void;
  isNewlyFilled?: boolean;
}) {
  const [hovered, setHovered] = useState(false);

  const ratio = topic.published / topic.minCoverage;
  const isCovered = ratio >= 1;
  const isPartial = ratio > 0 && ratio < 1;
  const isGap = ratio === 0;
  const hasAudio = (topic.audioCount ?? 0) > 0;

  const blockClass = isCovered ? 'wall-block-filled' : isPartial ? 'wall-block-partial' : 'wall-block-gap';
  const pct = Math.min(100, Math.round(ratio * 100));

  const archiveBrowseHref = `/archives/browse?era=${eraId}&topic=${encodeURIComponent(topic.label)}`;
  const contributionHref = `/contribution-submission?era=${eraId}&topic=${encodeURIComponent(topic.label)}`;

  // Gap blocks are clickable buttons that open the panel
  if (isGap) {
    return (
      <div
        className="relative"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <button
          type="button"
          onClick={() => onGapClick(topic, eraId, eraLabel)}
          className={`relative rounded-xl p-4 min-h-[100px] w-full flex flex-col justify-between transition-all duration-200 group cursor-pointer text-left ${blockClass}`}
          title={`${topic.label} — ${topic.minCoverage} contributions needed — Click to see story ideas`}
        >
          {/* Dashed gap indicator */}
          <div
            className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center opacity-60 group-hover:opacity-100 transition-opacity duration-150"
            style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            <Plus size={11} />
          </div>

          <span
            className="text-xs font-medium leading-snug pr-6"
            style={{ color: 'var(--muted-foreground)' }}
          >
            {topic.label}
          </span>

          <div>
            <div
              className="h-1 rounded-full w-full mt-3 mb-1.5"
              style={{ backgroundColor: 'var(--muted)' }}
            >
              <div
                className="h-full rounded-full"
                style={{ width: '0%', backgroundColor: '#E05A4733' }}
              />
            </div>
            <div className="flex items-center justify-between">
              <span
                className="text-xs tabular-nums"
                style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', fontSize: '0.65rem' }}
              >
                0/{topic.minCoverage}
              </span>
              <span
                className="text-xs tabular-nums font-medium"
                style={{ color: '#E05A4766', fontFamily: 'var(--font-mono)', fontSize: '0.65rem' }}
              >
                0%
              </span>
            </div>
          </div>

          {/* Hover hint */}
          {hovered && (
            <div
              className="absolute inset-0 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(224,90,71,0.08)' }}
            >
              <span
                className="text-xs font-medium px-2 py-1 rounded-lg"
                style={{ backgroundColor: 'var(--card)', color: '#E05A47', border: '1px solid #E05A4740' }}
              >
                View story ideas
              </span>
            </div>
          )}
        </button>
      </div>
    );
  }

  // Non-gap blocks (partial / covered) — unchanged behaviour
  return (
    <div
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Topic block */}
      <Link
        href={archiveBrowseHref}
        className={`relative rounded-xl p-4 min-h-[100px] flex flex-col justify-between transition-all duration-200 group cursor-pointer ${blockClass}${isNewlyFilled ? ' wall-block-newly-filled' : ''}`}
        title={`${topic.label} — ${topic.published}/${topic.minCoverage} contributions — Click to browse archives`}
      >
        {isCovered && (
          <div
            className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }}
          >
            <BookMarked size={10} />
          </div>
        )}

        {/* Audio indicator badge */}
        {hasAudio && !isGap && (
          <div
            className="absolute bottom-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full"
            style={{
              backgroundColor: isCovered ? 'rgba(255,255,255,0.18)' : 'rgba(243,156,18,0.18)',
              border: `1px solid ${isCovered ? 'rgba(255,255,255,0.3)' : 'rgba(243,156,18,0.4)'}`,
            }}
            title={`${topic.audioCount} audio submission${(topic.audioCount ?? 0) !== 1 ? 's' : ''}`}
          >
            <Volume2
              size={9}
              style={{ color: isCovered ? '#FFFFFFCC' : '#F39C12' }}
            />
            <span
              style={{
                color: isCovered ? '#FFFFFFCC' : '#F39C12',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.55rem',
                lineHeight: 1,
              }}
            >
              {topic.audioCount}
            </span>
          </div>
        )}

        <span
          className="text-xs font-medium leading-snug pr-6"
          style={{ color: isCovered ? '#FFFFFFCC' : isPartial ? '#A06800CC' : 'var(--muted-foreground)' }}
        >
          {topic.label}
        </span>

        <div>
          <div
            className="h-1 rounded-full w-full mt-3 mb-1.5"
            style={{ backgroundColor: isCovered ? '#FFFFFF22' : 'var(--muted)' }}
          >
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${pct}%`,
                backgroundColor: isCovered ? '#FFFFFFAA' : isPartial ? '#F39C1288' : '#E05A4733',
              }}
            />
          </div>

          <div className="flex items-center justify-between">
            <span
              className="text-xs tabular-nums"
              style={{
                color: isCovered ? '#FFFFFF77' : 'var(--muted-foreground)',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.65rem',
              }}
            >
              {topic.published}/{topic.minCoverage}
            </span>
            <span
              className="text-xs tabular-nums font-medium"
              style={{
                color: isCovered ? '#FFFFFFAA' : isPartial ? '#A06800' : '#E05A4766',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.65rem',
              }}
            >
              {pct}%
            </span>
          </div>
        </div>
      </Link>

      {/* Hover dropdown */}
      {hovered && (
        <div
          className="absolute left-0 top-full mt-1.5 z-50 rounded-xl shadow-xl border overflow-hidden"
          style={{
            minWidth: '200px',
            backgroundColor: 'var(--card)',
            borderColor: 'var(--border)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}
        >
          {/* Header */}
          <div
            className="px-3 py-2.5 border-b"
            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--muted)' }}
          >
            <p className="text-xs font-semibold truncate" style={{ color: 'var(--foreground)' }}>
              {topic.label}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <BarChart2 size={10} style={{ color: 'var(--muted-foreground)' }} />
              <span className="text-xs" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', fontSize: '0.65rem' }}>
                {topic.published} contribution{topic.published !== 1 ? 's' : ''} · {pct}% covered
              </span>
              {hasAudio && (
                <span
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full ml-1"
                  style={{ backgroundColor: 'rgba(243,156,18,0.15)', border: '1px solid rgba(243,156,18,0.3)' }}
                >
                  <Volume2 size={9} style={{ color: '#F39C12' }} />
                  <span style={{ color: '#F39C12', fontFamily: 'var(--font-mono)', fontSize: '0.6rem' }}>
                    {topic.audioCount} audio
                  </span>
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="py-1">
            <Link
              href={archiveBrowseHref}
              className="flex items-center gap-2.5 px-3 py-2 text-xs transition-colors duration-100"
              style={{ color: 'var(--foreground)' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--muted)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              onClick={(e) => e.stopPropagation()}
            >
              <BookMarked size={13} style={{ color: 'var(--primary)', flexShrink: 0 }} />
              <span>Browse archives</span>
              <ExternalLink size={10} className="ml-auto opacity-50" />
            </Link>

            <Link
              href={`/contributions?era=${eraId}&topic=${encodeURIComponent(topic.label)}`}
              className="flex items-center gap-2.5 px-3 py-2 text-xs transition-colors duration-100"
              style={{ color: 'var(--foreground)' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--muted)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              onClick={(e) => e.stopPropagation()}
            >
              <Eye size={13} style={{ color: '#6366F1', flexShrink: 0 }} />
              <span>Quick-view stories</span>
            </Link>

            <Link
              href={contributionHref}
              className="flex items-center gap-2.5 px-3 py-2 text-xs transition-colors duration-100"
              style={{ color: 'var(--foreground)' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--muted)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              onClick={(e) => e.stopPropagation()}
            >
              <Plus size={13} style={{ color: '#E05A47', flexShrink: 0 }} />
              <span>Add a contribution</span>
            </Link>
          </div>

          {/* Coverage status footer */}
          <div
            className="px-3 py-2 border-t"
            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--muted)' }}
          >
            <div className="flex items-center justify-between">
              <span
                className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                style={{
                  backgroundColor: isCovered ? '#2C5E3B22' : isPartial ? '#F39C1222' : '#E05A4722',
                  color: isCovered ? '#1E4028' : isPartial ? '#A06800' : '#B03020',
                  fontSize: '0.6rem',
                }}
              >
                {isCovered ? 'Covered' : isPartial ? 'Partial' : 'Gap'}
              </span>
              <span
                className="text-xs tabular-nums"
                style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', fontSize: '0.65rem' }}
              >
                {topic.published} / {topic.minCoverage} needed
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WallGrid({ eras, coverageFilter, newlyFilledTopics = new Set() }: WallGridProps) {
  const [expandedEras, setExpandedEras] = useState<Record<string, boolean>>(
    Object.fromEntries(eras.map((e) => [e.id, true]))
  );
  const [activeGap, setActiveGap] = useState<{
    topic: TopicData;
    eraId: string;
    eraLabel: string;
  } | null>(null);

  const toggleEra = (id: string) => {
    setExpandedEras((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleGapClick = (topic: TopicData, eraId: string, eraLabel: string) => {
    setActiveGap({ topic, eraId, eraLabel });
  };

  const handleClosePanel = () => setActiveGap(null);

  return (
    <>
      <div className="space-y-8">
        {eras.map((era) => {
          const filteredTopics = era.topics.filter((t) => {
            if (coverageFilter === 'gaps') return t.published < t.minCoverage;
            if (coverageFilter === 'covered') return t.published >= t.minCoverage;
            return true;
          });

          if (filteredTopics.length === 0) return null;

          const eraPublished = era.topics.reduce((sum, t) => sum + t.published, 0);
          const eraMinTotal = era.topics.reduce((sum, t) => sum + t.minCoverage, 0);
          const eraPercent = Math.min(100, Math.round((eraPublished / eraMinTotal) * 100));
          const isExpanded = expandedEras[era.id] !== false;

          return (
            <div
              key={era.id}
              className="rounded-2xl border overflow-visible"
              style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
            >
              {/* Era header */}
              <div className="flex items-center justify-between px-6 py-5">
                <button
                  onClick={() => toggleEra(era.id)}
                  className="flex-1 flex items-center gap-4 text-left transition-colors duration-150"
                >
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
                        {era.label}
                      </h3>
                      <span
                        className="text-xs font-mono px-2 py-0.5 rounded"
                        style={{ color: 'var(--muted-foreground)', backgroundColor: 'var(--muted)', fontFamily: 'var(--font-mono)' }}
                      >
                        {era.dateRange}
                      </span>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{
                          backgroundColor: eraPercent >= 80 ? '#2C5E3B22' : eraPercent >= 50 ? '#E05A4722' : '#F39C1222',
                          color: eraPercent >= 80 ? '#1E4028' : eraPercent >= 50 ? '#B03020' : '#A06800',
                        }}
                      >
                        {eraPercent}% covered
                      </span>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      {era.description}
                    </p>
                  </div>
                </button>

                <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                  <Link
                    href={`/archives/browse?era=${era.id}`}
                    className="hidden sm:flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border transition-all duration-150"
                    style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)', backgroundColor: 'transparent' }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--primary)';
                      (e.currentTarget as HTMLAnchorElement).style.color = 'var(--primary)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border)';
                      (e.currentTarget as HTMLAnchorElement).style.color = 'var(--muted-foreground)';
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <BookMarked size={11} />
                    Archives
                  </Link>
                  {/* Era coverage mini bar */}
                  <div className="hidden sm:flex items-center gap-2">
                    <div className="w-24 h-1.5 rounded-full" style={{ backgroundColor: 'var(--muted)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${eraPercent}%`,
                          backgroundColor: eraPercent >= 80 ? '#2C5E3B' : eraPercent >= 50 ? '#E05A47' : '#F39C12',
                        }}
                      />
                    </div>
                    <span className="text-xs tabular-nums" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', fontSize: '0.7rem' }}>
                      {eraPublished}/{eraMinTotal}
                    </span>
                  </div>
                  <button onClick={() => toggleEra(era.id)}>
                    {isExpanded ? (
                      <ChevronUp size={16} style={{ color: 'var(--muted-foreground)' }} />
                    ) : (
                      <ChevronDown size={16} style={{ color: 'var(--muted-foreground)' }} />
                    )}
                  </button>
                </div>
              </div>

              {/* Topics grid */}
              {isExpanded && (
                <div className="px-6 pb-6 border-t" style={{ borderColor: 'var(--border)' }}>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 pt-5">
                    {filteredTopics.map((topic) => (
                      <TopicBlock
                        key={topic.id}
                        topic={topic}
                        eraId={era.id}
                        eraLabel={era.label}
                        onGapClick={handleGapClick}
                        isNewlyFilled={newlyFilledTopics.has(topic.id)}
                      />
                    ))}

                    {/* Add contribution CTA block */}
                    <Link
                      href="/contribution-submission"
                      className="rounded-xl border-2 border-dashed p-4 min-h-[100px] flex flex-col items-center justify-center gap-2 transition-all duration-200 group"
                      style={{ borderColor: 'var(--border)' }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.borderColor = '#E05A4744';
                        (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#E05A4708';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border)';
                        (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                      }}
                    >
                      <Plus size={18} style={{ color: 'var(--muted-foreground)' }} />
                      <span className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
                        Add contribution
                      </span>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Gap detail panel */}
      {activeGap && (
        <GapPanel
          topic={activeGap.topic}
          eraId={activeGap.eraId}
          eraLabel={activeGap.eraLabel}
          onClose={handleClosePanel}
        />
      )}
    </>
  );
}